import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  fetchReunionesRanking, fetchReunionesInforme, fetchReunionesList,
  fetchActiveSellers, createReunion, updateReunion, deleteReunion,
  fetchReunionesDashboard, fetchReunionesComparativa, fetchReunionesAnual
} from '../../Redux/Actions/actions';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler, RadialLinearScale
} from 'chart.js';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';
import s from './reuniones.module.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale, Tooltip, Legend, Filler);

// ═══ CONSTANTS ═══
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const currentYear = new Date().getFullYear();
const currentMonthName = MESES[new Date().getMonth()];
const todayStr = new Date().toISOString().slice(0, 10);

// ═══ HELPERS ═══
const calcScore = (r) => (Number(r.ventasCerradas) || 0) * 3 + (Number(r.visitasRealizadas) || 0) * 2 + (Number(r.tasaciones) || 0) * 1.5 + (Number(r.propiedadesCaptadas) || 0) * 2;
const fmt$ = (n) => { if (!n) return '$0'; return '$' + Number(n).toLocaleString('es-AR'); };
const fmtK = (n) => { if (!n) return '0'; if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M'; if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'k'; return '$' + n; };
const formatDate = (d) => { if (!d) return '—'; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
const pad2 = (n) => String(n).padStart(2, '0');
const formatTimer = (t) => `${pad2(Math.floor(t / 3600))}:${pad2(Math.floor((t % 3600) / 60))}:${pad2(t % 60)}`;

const pill = (icon, label, val) => (
  <span className={s.kpiPill} key={label}>{icon}{label ? <span> {label} </span> : null}<strong className={s.kpiPillStrong}>{val}</strong></span>
);

// ═══ COLLAPSIBLE CARD (matches HTML .card) ═══
const CollapsibleCard = ({ icon, title, color = 'purple', defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={s.card}>
      <div className={`${s.cardHeader} ${color === 'bordo' ? s.cardHeaderBordo : s.cardHeaderPurple}`} onClick={() => setOpen(!open)}>
        <div className={s.cardHeaderLeft}>
          <div className={s.cardIcon}>{icon}</div>
          <span className={s.cardTitle}>{title}</span>
        </div>
        <span className={`${s.chevron} ${open ? s.chevronOpen : ''}`}>&#x25BE;</span>
      </div>
      {open && <div className={s.cardBody}>{children}</div>}
    </div>
  );
};

// ═══ DETAIL HELPERS (matches HTML d-sec / d-row) ═══
const DRow = ({ label, val }) => (
  <div className={s.dRow}><span className={s.dLabel}>{label}</span><span className={s.dVal}>{val !== undefined && val !== null && val !== '' ? val : '—'}</span></div>
);
const DSec = ({ title, children }) => (
  <div className={s.dSec}><div className={s.dSecTitle}>{title}</div>{children}</div>
);

// ═══════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════
const ReunionesStats = () => {
  const dispatch = useDispatch();
  const { reunionesRanking, reunionesInforme, reunionesList, activeSellers, estadisticasLoading, reunionCrudLoading, reunionesDashboard, reunionesComparativa, reunionesAnual } = useSelector(st => st);

  // ── View ──
  const [view, setView] = useState('nueva');
  const [year, setYear] = useState(currentYear);

  // ── Form (Nueva Reunión) ──
  const emptyForm = { sellerId: '', mes: currentMonthName, fecha: todayStr, director: '', ventasCerradas: '', montoVendido: '', visitasRealizadas: '', tasaciones: '', propiedadesCaptadas: '', posteosIG: '', compromisosDados: '', compromisosCumplidos: '', contactos: '', llamadas: '', ofertas: '', seguimientos: '', duenos: '', operaciones: '', propCerrar: '', proxCaptar: '', origenCapt: '', compradores: '', tipoBusqueda: '', bloqueos: '', objetivo: '', apoyo: '', notas: '' };
  const [form, setForm] = useState(emptyForm);
  const [formMsg, setFormMsg] = useState(null);
  const h = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // ── Timer ──
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => { if (timerRunning) { timerRef.current = setInterval(() => setTimerSeconds(ss => ss + 1), 1000); } else { clearInterval(timerRef.current); } return () => clearInterval(timerRef.current); }, [timerRunning]);

  // ── Precarga ──
  const [precarga, setPrecarga] = useState(null);
  const [precargaLoading, setPrecargaLoading] = useState(false);
  const fetchPrecarga = useCallback(async (sid) => {
    if (!sid) { setPrecarga(null); return; }
    setPrecargaLoading(true);
    try {
      const res = await axios.get(`/reuniones/precarga/${sid}`);
      setPrecarga(res.data?.data || res.data);
    } catch (err) {
      setPrecarga(null);
      const msg = (err?.message === 'Network Error' || !err?.response)
        ? 'Error de conectividad: no pudimos comunicarnos con el servidor. Verifica tu conexion o que el backend este activo.'
        : 'No se pudo cargar la precarga del agente.';
      setToast({ msg, ok: false });
    }
    finally { setPrecargaLoading(false); }
  }, []);

  // ── Filters: Historial ──
  const [hfAgente, setHfAgente] = useState('');
  const [hfMes, setHfMes] = useState('');
  const [hfDesde, setHfDesde] = useState('');
  const [hfHasta, setHfHasta] = useState('');

  // ── Filters: Resumen ──
  const [rMes, setRMes] = useState('');
  const [rDesde, setRDesde] = useState('');
  const [rHasta, setRHasta] = useState('');

  // ── Comparativa ──
  const [cmpA1, setCmpA1] = useState('');
  const [cmpA2, setCmpA2] = useState('');

  // ── Anual ──
  const [anualAgent, setAnualAgent] = useState('');

  // ── Expanded cards ──
  const [expanded, setExpanded] = useState({});
  const toggleRC = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // ── Edit modal ──
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // ── PDF Overlay ──
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfAgentId, setPdfAgentId] = useState(null);

  // ── Confirm delete ──
  const [confirmDel, setConfirmDel] = useState(null);

  // ── Toast ──
  const [toast, setToast] = useState(null);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); } }, [toast]);

  const buildRequestErrorMessage = useCallback((err, fallback = 'No se pudo completar la solicitud.') => {
    if (err?.response?.data?.message) return err.response.data.message;
    if (err?.message === 'Network Error' || !err?.response) {
      return 'Error de conectividad: no pudimos comunicarnos con el servidor. Verifica tu conexion o que el backend este activo.';
    }
    return fallback;
  }, []);

  const notifyRequestError = useCallback((err, fallback) => {
    setToast({ msg: buildRequestErrorMessage(err, fallback), ok: false });
  }, [buildRequestErrorMessage]);

  // ═══ DATA FETCHING ═══
  const sellers = activeSellers?.data || activeSellers || [];
  useEffect(() => {
    dispatch(fetchActiveSellers()).catch((err) => {
      notifyRequestError(err, 'No se pudieron cargar los agentes activos.');
    });
  }, [dispatch, notifyRequestError]);
  useEffect(() => {
    if (view === 'inicio') {
      dispatch(fetchReunionesDashboard()).catch((err) => {
        notifyRequestError(err, 'No se pudo cargar el dashboard de reuniones.');
      });
    }
  }, [dispatch, view, notifyRequestError]);
  useEffect(() => {
    if (view === 'resumen') {
      dispatch(fetchReunionesRanking({ mes: rMes || undefined, desde: rDesde || undefined, hasta: rHasta || undefined })).catch((err) => {
        notifyRequestError(err, 'No se pudo cargar el resumen del equipo.');
      });
    }
  }, [dispatch, view, rMes, rDesde, rHasta, notifyRequestError]);
  useEffect(() => {
    if (view === 'historial') {
      dispatch(fetchReunionesList({ sellerId: hfAgente || undefined, mes: hfMes || undefined, desde: hfDesde || undefined, hasta: hfHasta || undefined, limit: 100 })).catch((err) => {
        notifyRequestError(err, 'No se pudo cargar el historial de reuniones.');
      });
    }
  }, [dispatch, view, hfAgente, hfMes, hfDesde, hfHasta, notifyRequestError]);
  useEffect(() => {
    if (view === 'comparativa' && cmpA1 && cmpA2) {
      dispatch(fetchReunionesComparativa({ agent1: cmpA1, agent2: cmpA2 })).catch((err) => {
        notifyRequestError(err, 'No se pudo cargar la comparativa de agentes.');
      });
    }
  }, [dispatch, view, cmpA1, cmpA2, notifyRequestError]);
  useEffect(() => {
    if (view === 'anual' && anualAgent) {
      dispatch(fetchReunionesAnual(anualAgent, year)).catch((err) => {
        notifyRequestError(err, 'No se pudo cargar el informe anual.');
      });
    }
  }, [dispatch, view, anualAgent, year, notifyRequestError]);
  useEffect(() => {
    if (view === 'informe' && pdfAgentId) {
      dispatch(fetchReunionesInforme(pdfAgentId, year)).catch((err) => {
        notifyRequestError(err, 'No se pudo cargar el informe del agente.');
      });
    }
  }, [dispatch, view, pdfAgentId, year, notifyRequestError]);
  useEffect(() => { if (formMsg) { const t = setTimeout(() => setFormMsg(null), 4000); return () => clearTimeout(t); } }, [formMsg]);

  // ═══ COMPUTED DATA ═══
  const dashData = reunionesDashboard?.data || reunionesDashboard || {};
  const rankData = reunionesRanking?.data || reunionesRanking || {};
  const agentes = rankData.ranking || rankData.agentes || rankData || [];
  const promEquipo = (reunionesRanking?.promedioEquipo || rankData.promedioEquipo) || {};
  const cmpData = reunionesComparativa?.data || reunionesComparativa || {};
  const anualRaw = reunionesAnual?.data || reunionesAnual || {};
  const informeRaw = reunionesInforme?.data || reunionesInforme || {};
  const histList = reunionesList?.data || [];

  const convPreview = useMemo(() => {
    const v = Number(form.ventasCerradas) || 0;
    const vis = Number(form.visitasRealizadas) || 0;
    return vis > 0 ? (v / vis * 100).toFixed(1) + '%' : '—';
  }, [form.ventasCerradas, form.visitasRealizadas]);

  // ═══ HANDLERS ═══
  const showMsg = (text, ok) => setFormMsg({ text, ok });
  const showToast = (msg, ok = true) => setToast({ msg, ok });

  const handleAgentChange = (sid) => { h('sellerId', sid); fetchPrecarga(sid); };

  const resetForm = (silent) => {
    if (!silent && !window.confirm('¿Limpiar todos los campos?')) return;
    setForm({ ...emptyForm });
    setPrecarga(null); setTimerRunning(false); setTimerSeconds(0);
  };

  const handleGuardar = async () => {
    if (!form.sellerId) { showMsg('⚠️ Seleccioná un agente.', false); return; }
    if (!form.fecha) { showMsg('⚠️ Completá la fecha.', false); return; }
    const dur = timerSeconds > 0 ? formatTimer(timerSeconds) : undefined;
    const body = {
      sellerId: Number(form.sellerId), mes: form.mes, fecha: form.fecha, director: form.director || undefined,
      ventasCerradas: Number(form.ventasCerradas) || 0, montoVendido: Number(form.montoVendido) || 0,
      visitasRealizadas: Number(form.visitasRealizadas) || 0, tasaciones: Number(form.tasaciones) || 0,
      propiedadesCaptadas: Number(form.propiedadesCaptadas) || 0, posteosIG: Number(form.posteosIG) || 0,
      compromisosDados: Number(form.compromisosDados) || 0, compromisosCumplidos: Number(form.compromisosCumplidos) || 0,
      contactos: Number(form.contactos) || 0, llamadas: Number(form.llamadas) || 0, ofertas: Number(form.ofertas) || 0,
      seguimientos: Number(form.seguimientos) || 0, duenos: Number(form.duenos) || 0,
      operaciones: form.operaciones || undefined, propCerrar: form.propCerrar || undefined,
      proxCaptar: form.proxCaptar || undefined, origenCapt: form.origenCapt || undefined,
      compradores: Number(form.compradores) || 0, tipoBusqueda: form.tipoBusqueda || undefined,
      bloqueos: form.bloqueos || undefined, objetivo: form.objetivo || undefined, apoyo: form.apoyo || undefined,
      notas: form.notas || undefined, duracion: dur
    };
    try {
      await dispatch(createReunion(body));
      const ag = sellers.find(ss => ss.id === Number(form.sellerId));
      showMsg(`✅ Reunión de ${ag?.name || 'agente'} guardada correctamente.`, true);
      resetForm(true);
    } catch (err) {
      showMsg(`⚠️ ${buildRequestErrorMessage(err, 'Error al guardar la reunion.')}`, false);
    }
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setEditForm({ ventas: r.ventasCerradas || 0, monto: r.montoVendido || 0, visitas: r.visitasRealizadas || 0, vrc: r.tasaciones || 0, captadas: r.propiedadesCaptadas || 0, posteos: r.posteosIG || 0, compDados: r.compromisosDados || 0, compCumplidos: r.compromisosCumplidos || 0, objetivo: r.objetivo || '', notas: r.notas || '' });
    setEditOpen(true);
  };
  const closeEdit = () => { setEditOpen(false); setEditId(null); };
  const saveEdit = async () => {
    if (!editId) return;
    const body = { ventasCerradas: Number(editForm.ventas) || 0, montoVendido: Number(editForm.monto) || 0, visitasRealizadas: Number(editForm.visitas) || 0, tasaciones: Number(editForm.vrc) || 0, propiedadesCaptadas: Number(editForm.captadas) || 0, posteosIG: Number(editForm.posteos) || 0, compromisosDados: Number(editForm.compDados) || 0, compromisosCumplidos: Number(editForm.compCumplidos) || 0, objetivo: editForm.objetivo, notas: editForm.notas };
    try {
      await dispatch(updateReunion(editId, body));
      showToast('Reunión actualizada');
      closeEdit();
      refreshView();
    } catch (err) {
      showToast(buildRequestErrorMessage(err, 'Error al editar la reunion.'), false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteReunion(id));
      showToast('Reunión eliminada');
      setConfirmDel(null);
      refreshView();
    } catch (err) {
      showToast(buildRequestErrorMessage(err, 'Error al eliminar la reunion.'), false);
    }
  };

  const refreshView = () => {
    if (view === 'historial') {
      dispatch(fetchReunionesList({ sellerId: hfAgente || undefined, mes: hfMes || undefined, desde: hfDesde || undefined, hasta: hfHasta || undefined, limit: 100 })).catch((err) => {
        notifyRequestError(err, 'No se pudo actualizar el historial de reuniones.');
      });
    }
    if (view === 'resumen') {
      dispatch(fetchReunionesRanking({ mes: rMes || undefined })).catch((err) => {
        notifyRequestError(err, 'No se pudo actualizar el resumen del equipo.');
      });
    }
    if (view === 'inicio') {
      dispatch(fetchReunionesDashboard()).catch((err) => {
        notifyRequestError(err, 'No se pudo actualizar el dashboard de reuniones.');
      });
    }
    if (view === 'informe' && pdfAgentId) {
      dispatch(fetchReunionesInforme(pdfAgentId, year)).catch((err) => {
        notifyRequestError(err, 'No se pudo actualizar el informe del agente.');
      });
    }
  };

  const clearHistFilters = () => { setHfAgente(''); setHfMes(''); setHfDesde(''); setHfHasta(''); };

  const openPdfAgente = (sellerId) => { setPdfAgentId(sellerId); setView('informe'); };
  const preselectAgent = (sid) => { h('sellerId', String(sid)); fetchPrecarga(sid); setView('nueva'); };

  const sellerName = (sid) => { const ss = sellers.find(x => x.id === Number(sid)); return ss ? `${ss.name} ${ss.last_name || ''}`.trim() : `Agente #${sid}`; };

  // ═══════════════════════════════════════════════════
  //  NAVIGATION
  // ═══════════════════════════════════════════════════
  const Nav = () => (
    <nav className={s.nav}>
      <div className={s.navBrand}>
        <div>
          <div className={s.navTitle}>by Ra&iacute;ces</div>
          <div className={s.navSub}>Reuniones Individuales</div>
        </div>
      </div>
      <div className={s.navTabs}>
        {[
          { k: 'inicio', l: '🏠 Inicio' },
          { k: 'nueva', l: '➕ Nueva' },
          { k: 'historial', l: '📋 Historial' },
          { k: 'resumen', l: '📊 Resumen' }
        ].map(t => (
          <button key={t.k} className={`${s.navTab} ${view === t.k || (t.k === 'resumen' && (view === 'comparativa' || view === 'anual' || view === 'informe')) ? s.navTabActive : ''}`} onClick={() => setView(t.k)}>{t.l}</button>
        ))}
      </div>
    </nav>
  );

  // ═══════════════════════════════════════════════════
  //  PAGE: INICIO (Dashboard)
  // ═══════════════════════════════════════════════════
  const renderInicio = () => {
    const alertas = dashData.alertas || [];
    const recientes = dashData.recientes || [];
    const pendientes = dashData.pendientes || [];
    const badgeCls = (b) => b === 'ok' ? s.badgeOk : b === 'warn' ? s.badgeWarn : s.badgeDanger;
    return (
      <div className={s.page}>
        <div className={s.pageHeader}><h1 className={s.pageTitle}>Panel Principal</h1><p className={s.pageSub}>Alertas, recordatorios y últimas reuniones</p></div>
        {estadisticasLoading && !dashData.totalReuniones ? <div className={s.loading}>Cargando dashboard...</div> : <>
          {alertas.length > 0 && alertas.map((a, i) => (
            <div key={i} className={`${s.alert} ${s.alertWarn}`}>
              ⏰ <strong>{a.agente?.name} {a.agente?.last_name}</strong> &mdash; hace {a.diasSinReunion} d&iacute;as sin reuni&oacute;n.
              <button className={s.alertBtn} onClick={() => preselectAgent(a.sellerId)}>Nueva reuni&oacute;n</button>
            </div>
          ))}
          <div className={s.dashGrid}>
            <div className={s.dashCard}><div className={s.dashNum}>{dashData.totalReuniones || 0}</div><div className={s.dashLbl}>Reuniones</div></div>
            <div className={s.dashCard}><div className={s.dashNum}>{dashData.totalVentas || 0}</div><div className={s.dashLbl}>Ventas</div></div>
            <div className={s.dashCard}><div className={s.dashNum}>{fmtK(dashData.totalMonto)}</div><div className={s.dashLbl}>Monto Total</div></div>
          </div>
          <div className={s.dashRecent}><h4 className={s.dashRecentTitle}>📋 Últimas Reuniones</h4>
            {recientes.length ? recientes.map((r, i) => {
              const dias = r.diasDesdeReunion ?? Math.floor((new Date() - new Date(r.fecha)) / 86400000);
              return (
                <div key={i} className={s.dashRow}>
                  <div><div className={s.dashRowName}>{r.seller?.name} {r.seller?.last_name}</div><div className={s.dashRowMeta}>{formatDate(r.fecha)} &middot; {r.ventasCerradas || 0} ventas</div></div>
                  <span className={badgeCls(r.badge)}>hace {dias}d</span>
                </div>
              );
            }) : <p style={{ fontSize: 12, color: 'var(--gray)' }}>Sin reuniones.</p>}
          </div>
          <div className={s.dashRecent}><h4 className={s.dashRecentTitle}>⏰ Agentes sin reuni&oacute;n (+15 d&iacute;as)</h4>
            {pendientes.length ? pendientes.map((p, i) => (
              <div key={i} className={s.dashRow}><div className={s.dashRowName}>{p.agente?.name} {p.agente?.last_name}</div><span className={s.badgeDanger}>{p.diasSinReunion}d</span></div>
            )) : <p style={{ fontSize: 12, color: 'var(--success)' }}>✅ Todos al d&iacute;a.</p>}
          </div>
        </>}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════
  //  PAGE: NUEVA REUNIÓN
  // ═══════════════════════════════════════════════════
  const renderNueva = () => {
    const vrcAlert = precarga?.vrcAlert;
    const tend = precarga?.tendencias;
    const ultimaR = precarga?.ultimaReunion;
    const racha = precarga?.racha || 0;
    const diasSin = precarga?.diasSinVenta;

    const trendArrow = (cur, prev) => {
      if (prev == null) return null;
      const c = Number(cur) || 0;
      if (c > prev) return <span className={s.trendUp}>↑</span>;
      if (c < prev) return <span className={s.trendDown}>↓</span>;
      return <span className={s.trendFlat}>→</span>;
    };

    return (
      <>
        <div className={s.page}>
          <div className={s.pageHeader}><h1 className={s.pageTitle}>Nueva Reuni&oacute;n</h1><p className={s.pageSub}>Complet&aacute; los datos durante la reuni&oacute;n con el agente</p></div>

          {/* Timer */}
          <div className={s.timerBar}>
            <div><div className={s.timerLbl}>⏱ Duraci&oacute;n de la reuni&oacute;n</div><div className={`${s.timerDisplay} ${timerRunning ? s.timerDisplayRunning : ''}`}>{formatTimer(timerSeconds)}</div></div>
            {!timerRunning
              ? <button className={s.timerBtn} onClick={() => setTimerRunning(true)}>▶ Iniciar</button>
              : <button className={`${s.timerBtn} ${s.timerBtnStop}`} onClick={() => setTimerRunning(false)}>⏸ Pausar</button>
            }
            <button className={`${s.timerBtn} ${s.timerBtnReset}`} onClick={() => { setTimerRunning(false); setTimerSeconds(0); }}>↺ Reset</button>
            {timerRunning && <span className={s.timerLbl}>En curso...</span>}
          </div>

          {/* Message */}
          {formMsg && <div className={`${s.msg} ${formMsg.ok ? s.msgOk : s.msgError}`}>{formMsg.text}</div>}

          {/* Meta Card */}
          <div className={s.metaCard}>
            <h3 className={s.metaCardTitle}>📅 Datos de la Reuni&oacute;n</h3>
            <div className={s.metaGrid}>
              <div className={`${s.field} ${s.span2}`}>
                <label className={s.fieldLabel}>Agente</label>
                <select className={s.fieldInput} value={form.sellerId} onChange={e => handleAgentChange(e.target.value)}>
                  <option value="">&mdash; Seleccion&aacute; un agente &mdash;</option>
                  {sellers.map(ss => <option key={ss.id} value={ss.id}>{ss.name} {ss.last_name || ''}</option>)}
                </select>
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Fecha</label>
                <input type="date" className={s.fieldInput} value={form.fecha} onChange={e => h('fecha', e.target.value)} />
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Mes</label>
                <select className={s.fieldInput} value={form.mes} onChange={e => h('mes', e.target.value)}>
                  {MESES.map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div className={`${s.field} ${s.span2}`}>
                <label className={s.fieldLabel}>Director a cargo de la reuni&oacute;n</label>
                <input type="text" className={s.fieldInput} placeholder="Nombre del director" value={form.director} onChange={e => h('director', e.target.value)} />
              </div>
            </div>
            {/* Precarga info */}
            {precargaLoading && <div className={s.precargaInfo}>⏳ Cargando datos del agente...</div>}
            {precarga && ultimaR && (
              <div style={{ marginTop: 6 }}>
                <div className={`${s.alert} ${s.alertInfo}`}>
                  📊 &Uacute;ltima reuni&oacute;n: <strong>{formatDate(ultimaR.fecha)}</strong>
                  {diasSin > 0 && <> &middot; Sin venta: <strong style={{ color: 'var(--warn)' }}>{diasSin} d&iacute;as</strong></>}
                  {racha > 1 && <> &middot; <span className={s.rachaBadge}>🔥 {racha} en racha</span></>}
                </div>
              </div>
            )}
          </div>

          {/* VRC Alert */}
          {vrcAlert && <div className={`${s.alert} ${s.alertWarn}`}>⚠️ Este agente lleva 2 o m&aacute;s reuniones consecutivas con 0 VRC. Revis&aacute; su captaci&oacute;n.</div>}

          {/* KPI Section */}
          <div className={s.kpiSection}>
            <h4 className={s.kpiSectionTitle}>⭐ M&eacute;tricas Principales</h4>
            <div className={s.kpiGrid}>
              <div className={s.kpiItem}><label className={s.kpiItemLabel}>Ventas</label><input type="number" className={s.kpiItemInput} min="0" placeholder="0" value={form.ventasCerradas} onChange={e => h('ventasCerradas', e.target.value)} />{tend && trendArrow(form.ventasCerradas, tend.ventasCerradas)}</div>
              <div className={s.kpiItem}><label className={s.kpiItemLabel}>Visitas</label><input type="number" className={s.kpiItemInput} min="0" placeholder="0" value={form.visitasRealizadas} onChange={e => h('visitasRealizadas', e.target.value)} />{tend && trendArrow(form.visitasRealizadas, tend.visitasRealizadas)}</div>
              <div className={s.kpiItem}><label className={s.kpiItemLabel}>VRC</label><input type="number" className={s.kpiItemInput} min="0" placeholder="0" value={form.tasaciones} onChange={e => h('tasaciones', e.target.value)} />{tend && trendArrow(form.tasaciones, tend.tasaciones)}</div>
              <div className={s.kpiItem}><label className={s.kpiItemLabel}>Captadas</label><input type="number" className={s.kpiItemInput} min="0" placeholder="0" value={form.propiedadesCaptadas} onChange={e => h('propiedadesCaptadas', e.target.value)} />{tend && trendArrow(form.propiedadesCaptadas, tend.propiedadesCaptadas)}</div>
            </div>
            <div className={s.kpiGrid3}>
              <div className={s.kpiItem}><label className={s.kpiItemLabel}>Monto $</label><input type="number" className={s.kpiItemInput} min="0" placeholder="0" style={{ fontSize: 15 }} value={form.montoVendido} onChange={e => h('montoVendido', e.target.value)} /></div>
              <div className={s.kpiItem}><label className={s.kpiItemLabel}>Posteos IG</label><input type="number" className={s.kpiItemInput} min="0" placeholder="0" value={form.posteosIG} onChange={e => h('posteosIG', e.target.value)} /></div>
              <div className={`${s.kpiItem} ${s.kpiItemConv}`}><label className={s.kpiItemLabel} style={{ color: 'var(--purple)' }}>Conv. V&rarr;V</label><input type="text" className={`${s.kpiItemInput} ${s.kpiItemReadonly}`} readOnly value={convPreview} style={{ fontSize: 15 }} /></div>
            </div>
          </div>

          {/* Producción */}
          <CollapsibleCard icon="📦" title="Producci&oacute;n" color="purple" defaultOpen>
            <div className={s.fieldsGrid}>
              <div className={s.field}><label className={s.fieldLabel}>Contactos nuevos</label><input type="number" className={s.fieldInput} min="0" placeholder="0" value={form.contactos} onChange={e => h('contactos', e.target.value)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Llamadas / mensajes</label><input type="number" className={s.fieldInput} min="0" placeholder="0" value={form.llamadas} onChange={e => h('llamadas', e.target.value)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Ofertas recibidas / presentadas</label><input type="number" className={s.fieldInput} min="0" placeholder="0" value={form.ofertas} onChange={e => h('ofertas', e.target.value)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Seguimientos a clientes</label><input type="number" className={s.fieldInput} min="0" placeholder="0" value={form.seguimientos} onChange={e => h('seguimientos', e.target.value)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Due&ntilde;os nuevos conocidos</label><input type="number" className={s.fieldInput} min="0" placeholder="0" value={form.duenos} onChange={e => h('duenos', e.target.value)} /></div>
            </div>
          </CollapsibleCard>

          {/* Ventas */}
          <CollapsibleCard icon="💰" title="Ventas" color="bordo">
            <div className={s.fieldsGrid}>
              <div className={`${s.field} ${s.span2}`}><label className={s.fieldLabel}>Operaciones activas en trabajo</label><input type="text" className={s.fieldInput} placeholder="Ej: Venta casa Chacras, depto Las Heras..." value={form.operaciones} onChange={e => h('operaciones', e.target.value)} /></div>
              <div className={`${s.field} ${s.span2}`}><label className={s.fieldLabel}>Propiedad m&aacute;s cerca de cerrarse</label><input type="text" className={s.fieldInput} placeholder="Direcci&oacute;n o descripci&oacute;n..." value={form.propCerrar} onChange={e => h('propCerrar', e.target.value)} /></div>
            </div>
          </CollapsibleCard>

          {/* Captaciones */}
          <CollapsibleCard icon="🏠" title="Captaciones" color="purple">
            <div className={s.fieldsGrid}>
              <div className={`${s.field} ${s.span2}`}><label className={s.fieldLabel}>Propiedades pr&oacute;ximas a captar</label><textarea className={`${s.fieldInput} ${s.fieldTextarea}`} placeholder="List&aacute; las propiedades..." value={form.proxCaptar} onChange={e => h('proxCaptar', e.target.value)} /></div>
              <div className={`${s.field} ${s.span2}`}><label className={s.fieldLabel}>Origen de captaciones</label><input type="text" className={s.fieldInput} placeholder="Ej: 70% referidos, 30% portales" value={form.origenCapt} onChange={e => h('origenCapt', e.target.value)} /></div>
            </div>
          </CollapsibleCard>

          {/* Búsquedas */}
          <CollapsibleCard icon="🔍" title="B&uacute;squedas" color="bordo">
            <div className={s.fieldsGrid}>
              <div className={s.field}><label className={s.fieldLabel}>Compradores activos</label><input type="number" className={s.fieldInput} min="0" placeholder="0" value={form.compradores} onChange={e => h('compradores', e.target.value)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Tipo de propiedad buscada</label><input type="text" className={s.fieldInput} placeholder="Casa / depto / terreno..." value={form.tipoBusqueda} onChange={e => h('tipoBusqueda', e.target.value)} /></div>
            </div>
          </CollapsibleCard>

          {/* Bloqueos */}
          <CollapsibleCard icon="🚧" title="Bloqueos" color="purple">
            <div className={s.field}><label className={s.fieldLabel}>Situaciones trabadas o dificultades</label><textarea className={`${s.fieldInput} ${s.fieldTextarea}`} placeholder="Describ&iacute; lo que necesita desbloquearse..." value={form.bloqueos} onChange={e => h('bloqueos', e.target.value)} /></div>
          </CollapsibleCard>

          {/* Compromisos */}
          <CollapsibleCard icon="✅" title="Compromisos y Plan de Acci&oacute;n" color="bordo">
            <div className={s.fieldsGrid}>
              <div className={s.field}><label className={s.fieldLabel}>Compromisos dados hoy <span className={s.fieldBadge}>&rarr; Comp. Dados</span></label><input type="number" className={s.fieldInput} min="0" placeholder="0" value={form.compromisosDados} onChange={e => h('compromisosDados', e.target.value)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Compromisos ant. cumplidos <span className={s.fieldBadge}>&rarr; Comp. Cumpl.</span></label><input type="number" className={s.fieldInput} min="0" placeholder="0" value={form.compromisosCumplidos} onChange={e => h('compromisosCumplidos', e.target.value)} /></div>
              <div className={`${s.field} ${s.span2}`}><label className={s.fieldLabel}>Objetivo principal de la semana</label><input type="text" className={s.fieldInput} placeholder="¿Qué va a lograr esta semana?" value={form.objetivo} onChange={e => h('objetivo', e.target.value)} /></div>
              <div className={`${s.field} ${s.span2}`}><label className={s.fieldLabel}>Apoyo solicitado al l&iacute;der</label><input type="text" className={s.fieldInput} placeholder="¿Qué necesita de tu parte?" value={form.apoyo} onChange={e => h('apoyo', e.target.value)} /></div>
            </div>
          </CollapsibleCard>

          {/* Notas */}
          <div className={s.notasBox}>
            <h4 className={s.notasBoxTitle}>📝 Notas Libres de la Reuni&oacute;n</h4>
            <textarea className={s.notasBoxTextarea} placeholder="Cualquier observaci&oacute;n adicional, acuerdos, situaciones..." value={form.notas} onChange={e => h('notas', e.target.value)} />
          </div>
          <div style={{ height: 80 }} />
        </div>

        {/* Sticky Footer */}
        <div className={s.stickyFooter}>
          <button className={s.btnSecondary} onClick={() => resetForm(false)}>🔄 Limpiar</button>
          <button className={s.btnPrimary} onClick={handleGuardar} disabled={reunionCrudLoading}>{reunionCrudLoading ? 'Guardando...' : '💾 Guardar Reunión'}</button>
        </div>
      </>
    );
  };

  // ═══════════════════════════════════════════════════
  //  PAGE: HISTORIAL
  // ═══════════════════════════════════════════════════
  const renderHistorial = () => (
    <div className={s.page}>
      <div className={s.pageHeader}><h1 className={s.pageTitle}>Historial de Reuniones</h1><p className={s.pageSub}>Filtr&aacute; por agente, mes o rango de fechas</p></div>
      <div className={s.filterBar}>
        <select className={s.filterBarInput} value={hfAgente} onChange={e => setHfAgente(e.target.value)}>
          <option value="">Todos los agentes</option>
          {sellers.map(ss => <option key={ss.id} value={ss.id}>{ss.name} {ss.last_name || ''}</option>)}
        </select>
        <select className={s.filterBarInput} value={hfMes} onChange={e => setHfMes(e.target.value)}>
          <option value="">Todos los meses</option>
          {MESES.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className={s.filterGroup}><label className={s.filterBarLabel}>Desde</label><input type="date" className={s.filterBarInput} value={hfDesde} onChange={e => setHfDesde(e.target.value)} /></div>
        <div className={s.filterGroup}><label className={s.filterBarLabel}>Hasta</label><input type="date" className={s.filterBarInput} value={hfHasta} onChange={e => setHfHasta(e.target.value)} /></div>
        <button className={s.btnSecondary} onClick={clearHistFilters} style={{ padding: '7px 12px', fontSize: 11 }}>✕ Limpiar filtros</button>
      </div>
      {estadisticasLoading ? <div className={s.loading}>Cargando...</div> : histList.length === 0 ? (
        <div className={s.emptyState}><div className={s.emptyEmoji}>📋</div><p className={s.emptyText}>Sin reuniones.</p></div>
      ) : histList.map(r => {
        const name = r.seller ? `${r.seller.name} ${r.seller.last_name || ''}` : `Agente #${r.sellerId}`;
        const dur = r.duracion ? ` · ⏱${r.duracion}` : '';
        const dir = r.director ? ` · ${r.director}` : '';
        const isExp = expanded[r.id];
        const cv = (r.visitasRealizadas || 0) > 0 ? ((r.ventasCerradas || 0) / r.visitasRealizadas * 100).toFixed(1) + '%' : '—';
        const pc = (r.compromisosDados || 0) > 0 ? Math.round((r.compromisosCumplidos || 0) / r.compromisosDados * 100) + '%' : '—';
        const editLog = r.editHistory?.length ? <div style={{ fontSize: 10, color: 'var(--warn)', marginBottom: 4 }}>✏️ Editado {r.editHistory.length}x &mdash; &uacute;ltimo: {r.editHistory[r.editHistory.length - 1]}</div> : null;
        return (
          <div key={r.id} className={s.reunionCard} onClick={() => toggleRC(r.id)}>
            <div className={s.reunionCardHeader}>
              <div><div className={s.reunionAgent}>{name}</div><div className={s.reunionDate}>{formatDate(r.fecha)} &mdash; {r.mes}{dir}{dur}</div></div>
              {!isExp && <span className={s.rcToggleLbl}>Ver detalle ›</span>}
            </div>
            <div className={s.reunionKpis}>
              {pill('🤝', 'Ventas', r.ventasCerradas || 0)}
              {pill('💵', '$', fmt$(r.montoVendido))}
              {pill('👁️', '', r.visitasRealizadas || 0)}
              {pill('📋', '', r.tasaciones || 0)}
              {pill('🏠', '', r.propiedadesCaptadas || 0)}
              {pill('📱', '', r.posteosIG || 0)}
              {pill('✅', '', `${r.compromisosDados || 0}/${r.compromisosCumplidos || 0}`)}
            </div>
            {isExp && (
              <div className={s.reunionDetail} onClick={e => e.stopPropagation()}>
                <div className={s.dSections}>
                  <DSec title="📊 Producci&oacute;n"><DRow label="Visitas realizadas" val={r.visitasRealizadas || 0} /><DRow label="Conversi&oacute;n" val={cv} /></DSec>
                  <DSec title="🤝 Ventas"><DRow label="Ventas cerradas" val={r.ventasCerradas || 0} /><DRow label="Monto total" val={fmt$(r.montoVendido)} /><DRow label="Operaciones en curso" val={r.operaciones} /><DRow label="Propiedades a cerrar" val={r.propCerrar} /></DSec>
                  <DSec title="🏠 Captaciones"><DRow label="VRC (Tasaciones)" val={r.tasaciones || 0} /><DRow label="Captadas" val={r.propiedadesCaptadas || 0} /><DRow label="Posteos" val={r.posteosIG || 0} /><DRow label="Pr&oacute;ximas captaciones" val={r.proxCaptar} /><DRow label="Origen captaci&oacute;n" val={r.origenCapt} /></DSec>
                  <DSec title="🔍 B&uacute;squedas"><DRow label="Contactos" val={r.contactos || 0} /><DRow label="Llamadas" val={r.llamadas || 0} /><DRow label="Ofertas" val={r.ofertas || 0} /><DRow label="Seguimientos" val={r.seguimientos || 0} /><DRow label="Due&ntilde;os contactados" val={r.duenos || 0} /><DRow label="Compradores activos" val={r.compradores || 0} /><DRow label="Tipo de b&uacute;squeda" val={r.tipoBusqueda} /></DSec>
                  <DSec title="🚧 Bloqueos"><div className={s.dText}>{r.bloqueos || 'Sin bloqueos registrados.'}</div></DSec>
                  <DSec title="🎯 Plan de Acci&oacute;n"><DRow label="Objetivo" val={r.objetivo} /><DRow label="Apoyo requerido" val={r.apoyo} /></DSec>
                  <DSec title="✅ Compromisos"><DRow label="Dados" val={r.compromisosDados || 0} /><DRow label="Cumplidos" val={r.compromisosCumplidos || 0} /><DRow label="% Cumplimiento" val={pc} /></DSec>
                </div>
                {r.notas && <DSec title="📝 Notas Libres"><div className={s.dText}>{r.notas}</div></DSec>}
                {editLog}
                <div className={s.detailActions}>
                  <button className={s.btnInforme} onClick={() => openPdfAgente(r.sellerId)}>📄 Informe</button>
                  <button className={s.btnEdit} onClick={() => openEdit(r)}>✏️ Editar</button>
                  <button className={s.btnDelete} onClick={() => setConfirmDel({ id: r.id, label: `Reunión de ${name} (${r.mes})` })}>🗑</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ═══════════════════════════════════════════════════
  //  PAGE: RESUMEN (Ranking)
  // ═══════════════════════════════════════════════════
  const renderResumen = () => {
    const list = Array.isArray(agentes) ? agentes : [];
    const totR = reunionesRanking?.totalReuniones || 0;
    const totV = reunionesRanking?.totalVentas || 0;
    const totM = reunionesRanking?.totalMonto || 0;
    const totVis = reunionesRanking?.totalVisitas || 0;
    const totVRC = reunionesRanking?.totalVRC || 0;
    const totCap = reunionesRanking?.totalCaptadas || 0;
    const avgScore = promEquipo.score || 0;

    const semCls = (sem) => sem === 'green' ? s.semGreen : sem === 'yellow' ? s.semYellow : s.semRed;

    // Chart data
    const chartLabels = list.slice(0, 10).map(a => `${a.agente?.name || ''} ${a.agente?.last_name || ''}`.trim());
    const chartData = list.length > 0 ? {
      labels: chartLabels,
      datasets: [
        { label: 'Ventas', data: list.slice(0, 10).map(a => a.ventasCerradas || 0), backgroundColor: '#4B3A8C', borderRadius: 4 },
        { label: 'VRC', data: list.slice(0, 10).map(a => a.tasaciones || 0), backgroundColor: '#8C3A5A', borderRadius: 4 }
      ]
    } : null;
    const chartOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true }, x: { grid: { display: false } } } };

    return (
      <div className={s.page}>
        <div className={s.pageHeader}><h1 className={s.pageTitle}>Resumen del Equipo</h1><p className={s.pageSub}>M&eacute;tricas acumuladas y ranking por agente</p></div>
        <div className={s.filterBar} style={{ marginBottom: 16 }}>
          <select className={s.filterBarInput} value={rMes} onChange={e => setRMes(e.target.value)}>
            <option value="">Todos los meses</option>
            {MESES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <div className={s.filterGroup}><label className={s.filterBarLabel}>Desde</label><input type="date" className={s.filterBarInput} value={rDesde} onChange={e => setRDesde(e.target.value)} /></div>
          <div className={s.filterGroup}><label className={s.filterBarLabel}>Hasta</label><input type="date" className={s.filterBarInput} value={rHasta} onChange={e => setRHasta(e.target.value)} /></div>
          <button className={s.btnSecondary} style={{ padding: '7px 12px', fontSize: 11 }} onClick={() => setView('comparativa')}>⚔ Comparar</button>
        </div>
        {estadisticasLoading ? <div className={s.loading}>Cargando...</div> : list.length === 0 ? (
          <div className={s.emptyState}><div className={s.emptyEmoji}>📊</div><p className={s.emptyText}>Sin datos.</p></div>
        ) : <>
          <div className={s.statsGrid}>
            <div className={s.statCard}><div className={s.statNum}>{totR}</div><div className={s.statLabel}>Reuniones</div></div>
            <div className={s.statCard}><div className={s.statNum}>{totV}</div><div className={s.statLabel}>Ventas</div></div>
            <div className={s.statCard}><div className={s.statNum}>{fmtK(totM)}</div><div className={s.statLabel}>Monto</div></div>
            <div className={s.statCard}><div className={s.statNum}>{totVis}</div><div className={s.statLabel}>Visitas</div></div>
            <div className={s.statCard}><div className={s.statNum}>{totVRC}</div><div className={s.statLabel}>VRC</div></div>
            <div className={s.statCard}><div className={s.statNum}>{totCap}</div><div className={s.statLabel}>Captadas</div></div>
          </div>
          {chartData && (
            <div className={s.chartWrap}><h4 className={s.chartWrapTitle}>Ventas vs VRC por Agente</h4><div className={s.chartInner}><Bar data={chartData} options={chartOpts} /></div></div>
          )}
          <div className={s.pageHeader} style={{ marginTop: 4 }}><h1 className={s.pageTitle} style={{ fontSize: 18 }}>Ranking del Equipo</h1></div>
          {list.map((a, i) => {
            const name = `${a.agente?.name || ''} ${a.agente?.last_name || ''}`.trim() || '—';
            const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;
            const score = a.score || 0;
            const ins = a.insignias || [];
            return (
              <div key={a.agente?.id || i} className={s.rankingCard}>
                <div className={s.rankingHeader}>
                  <div>
                    <div className={s.reunionAgent}>
                      <span className={`${s.semaforo} ${semCls(a.semaforo)}`} />
                      {medal} {name}
                      {(a.racha || 0) >= 3 && <span className={s.rachaBadge}> 🔥 {a.racha}</span>}
                      {a.diasSinVenta > 30 && <span className={s.diasSinVentaWarn}> ⚠️ {a.diasSinVenta}d</span>}
                    </div>
                    <div className={s.reunionDate}>{a.totalReuniones || 0} reuni{(a.totalReuniones || 0) !== 1 ? 'ones' : 'ón'} &middot; Score: {score.toFixed(0)}</div>
                    {ins.length > 0 && <div style={{ marginTop: 3 }}>{ins.slice(0, 3).map((b, j) => <span key={j} className={s.insignia}>{b.icono} {b.label}</span>)}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className={s.rankingInformeBtn} onClick={() => openPdfAgente(a.agente?.id)}>📄 Informe</button>
                    <button className={s.btnEdit} onClick={() => { setAnualAgent(String(a.agente?.id)); setView('anual'); }}>📊 Anual</button>
                    <button className={s.btnInforme} onClick={() => preselectAgent(a.agente?.id)}>➕ Cargar</button>
                  </div>
                </div>
                <div className={s.reunionKpis}>
                  {pill('🤝', 'Ventas', a.ventasCerradas || 0)}
                  {pill('💵', '', fmtK(a.montoVendido))}
                  {pill('👁️', '', a.visitasRealizadas || 0)}
                  {pill('📋', '', a.tasaciones || 0)}
                  {pill('🏠', '', a.propiedadesCaptadas || 0)}
                </div>
              </div>
            );
          })}
        </>}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════
  //  PAGE: COMPARATIVA
  // ═══════════════════════════════════════════════════
  const renderComparativa = () => {
    const a1 = cmpData.agente1 || {};
    const a2 = cmpData.agente2 || {};
    const metrics = [{ k: 'ventas', l: 'Ventas' }, { k: 'monto', l: 'Monto $', fmt: true }, { k: 'visitas', l: 'Visitas' }, { k: 'vrc', l: 'VRC' }, { k: 'captadas', l: 'Captadas' }, { k: 'posteos', l: 'Posteos IG' }, { k: 'reuniones', l: 'Reuniones' }];
    const makeCard = (ag, d) => (
      <div className={s.compareAgentCard}>
        <h4 className={s.compareAgentCardTitle}>{ag?.name} {ag?.last_name}</h4>
        {metrics.map(m => { const v = d[m.k] || 0; return <div key={m.k} className={s.compareRow}><span>{m.l}</span><span className={s.compareVal}>{m.fmt ? fmt$(v) : v}</span></div>; })}
      </div>
    );
    const hasData = a1.agente && a2.agente;
    const cmpChartData = hasData ? {
      labels: ['Ventas', 'Visitas', 'VRC', 'Captadas'],
      datasets: [
        { label: `${a1.agente?.name || ''}`, data: [a1.ventas || 0, a1.visitas || 0, a1.vrc || 0, a1.captadas || 0], backgroundColor: '#4B3A8C', borderRadius: 4 },
        { label: `${a2.agente?.name || ''}`, data: [a2.ventas || 0, a2.visitas || 0, a2.vrc || 0, a2.captadas || 0], backgroundColor: '#8C3A5A', borderRadius: 4 }
      ]
    } : null;
    return (
      <div className={s.page}>
        <div className={s.pageHeader}><h1 className={s.pageTitle}>Comparar Agentes</h1><p className={s.pageSub}>Analiz&aacute; dos agentes lado a lado</p></div>
        <div className={s.metaCard}><h3 className={s.metaCardTitle}>Seleccion&aacute; dos agentes</h3>
          <div className={s.metaGrid}>
            <div className={s.field}><label className={s.fieldLabel}>Agente 1</label><select className={s.fieldInput} value={cmpA1} onChange={e => setCmpA1(e.target.value)}><option value="">&mdash; Agente 1 &mdash;</option>{sellers.map(ss => <option key={ss.id} value={ss.id}>{ss.name} {ss.last_name || ''}</option>)}</select></div>
            <div className={s.field}><label className={s.fieldLabel}>Agente 2</label><select className={s.fieldInput} value={cmpA2} onChange={e => setCmpA2(e.target.value)}><option value="">&mdash; Agente 2 &mdash;</option>{sellers.map(ss => <option key={ss.id} value={ss.id}>{ss.name} {ss.last_name || ''}</option>)}</select></div>
          </div>
        </div>
        {cmpA1 && cmpA2 && cmpA1 === cmpA2 && <div className={`${s.alert} ${s.alertWarn}`}>Seleccion&aacute; dos agentes diferentes.</div>}
        {hasData && <>
          <div className={s.compareGrid}>{makeCard(a1.agente, a1)}{makeCard(a2.agente, a2)}</div>
          {cmpChartData && <div className={s.chartWrap}><h4 className={s.chartWrapTitle}>Comparativa Visual</h4><div className={s.chartInner}><Bar data={cmpChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true }, x: { grid: { display: false } } } }} /></div></div>}
        </>}
        <button className={s.btnSecondary} onClick={() => setView('resumen')} style={{ marginTop: 12 }}>← Volver a Resumen</button>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════
  //  PAGE: ANUAL
  // ═══════════════════════════════════════════════════
  const renderAnual = () => {
    const ag = anualRaw.agente || {};
    const tot = anualRaw.totales || {};
    const mejorMes = anualRaw.mejorMes;
    const radar = anualRaw.radar || {};
    const evo = anualRaw.evolucionMensual || [];
    const tabla = anualRaw.tablaMensual || [];
    const ins = anualRaw.insignias || [];
    const rch = anualRaw.racha || 0;
    const agName = `${ag.name || ''} ${ag.last_name || ''}`.trim() || 'Agente';
    const pctComp = tot.pctCompromisos || (tot.compromisosDados > 0 ? Math.round(tot.compromisosCumplidos / tot.compromisosDados * 100) : 0);

    const radarData = Object.keys(radar).length > 0 ? {
      labels: ['Ventas', 'Visitas', 'VRC', 'Captadas', 'Posteos', 'Compromisos'],
      datasets: [{ label: agName, data: [radar.ventas || 0, radar.visitas || 0, radar.vrc || 0, radar.captadas || 0, radar.posteos || 0, radar.compromisos || 0], backgroundColor: 'rgba(75,58,140,0.2)', borderColor: '#4B3A8C', borderWidth: 2, pointBackgroundColor: '#4B3A8C', pointRadius: 4 }]
    } : null;
    const evoData = evo.length > 0 ? {
      labels: evo.map(e => e.mes),
      datasets: [
        { label: 'Score', data: evo.map(e => e.score?.toFixed(1) || 0), borderColor: '#4B3A8C', backgroundColor: 'rgba(75,58,140,0.08)', borderWidth: 2.5, pointRadius: 4, tension: 0.3, fill: true },
        { label: 'Ventas', data: evo.map(e => e.ventas || 0), borderColor: '#8C3A5A', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 3, borderDash: [4, 3], tension: 0.3 }
      ]
    } : null;

    return (
      <div className={s.page}>
        <div className={s.pageHeader}><h1 className={s.pageTitle}>Resumen Anual por Agente</h1><p className={s.pageSub}>Radar, evoluci&oacute;n y estad&iacute;sticas del agente</p></div>
        <div className={s.metaCard}><h3 className={s.metaCardTitle}>Seleccion&aacute; un agente</h3>
          <div className={s.field}><select className={s.fieldInput} value={anualAgent} onChange={e => setAnualAgent(e.target.value)}>
            <option value="">&mdash; Seleccion&aacute; &mdash;</option>
            {sellers.map(ss => <option key={ss.id} value={ss.id}>{ss.name} {ss.last_name || ''}</option>)}
          </select></div>
        </div>
        {anualAgent && !anualRaw.totales && estadisticasLoading && <div className={s.loading}>Cargando...</div>}
        {anualAgent && !estadisticasLoading && !anualRaw.totales && <div className={s.emptyState}><div className={s.emptyEmoji}>📅</div><p className={s.emptyText}>Sin datos.</p></div>}
        {anualRaw.totales && <>
          <div className={s.statsGrid}>
            <div className={s.statCard}><div className={s.statNum}>{tot.reuniones || 0}</div><div className={s.statLabel}>Reuniones</div></div>
            <div className={s.statCard}><div className={s.statNum}>{tot.ventas || 0}</div><div className={s.statLabel}>Ventas</div></div>
            <div className={s.statCard}><div className={s.statNum}>{fmtK(tot.monto)}</div><div className={s.statLabel}>Monto</div></div>
            <div className={s.statCard}><div className={s.statNum}>{tot.visitas || 0}</div><div className={s.statLabel}>Visitas</div></div>
            <div className={s.statCard}><div className={s.statNum}>{tot.vrc || 0}</div><div className={s.statLabel}>VRC</div></div>
            <div className={s.statCard}><div className={s.statNum}>{pctComp}%</div><div className={s.statLabel}>Compromisos</div></div>
          </div>
          {mejorMes && <div className={`${s.alert} ${s.alertSuccess}`}>🏆 Mejor mes hist&oacute;rico: <strong>{mejorMes.mes}</strong> &mdash; Score {mejorMes.score?.toFixed(0)}</div>}
          {rch >= 2 && <div className={`${s.alert} ${s.alertInfo}`}>🔥 Racha activa: <strong>{rch} reuniones seguidas</strong></div>}
          {ins.length > 0 && <div className={s.metaCard}><h3 className={s.metaCardTitle}>🏅 Insignias</h3><div>{ins.map((b, j) => <span key={j} className={s.insignia}>{b.icono} {b.label}</span>)}</div></div>}
          {radarData && <div className={s.radarWrap}><h4 className={s.radarWrapTitle}>Radar Anual &mdash; {agName}</h4><div className={s.radarInner}><Radar data={radarData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { r: { beginAtZero: true, grid: { color: '#E0E0F0' }, pointLabels: { font: { size: 11 } } } } }} /></div></div>}
          {evoData && <div className={s.chartWrap}><h4 className={s.chartWrapTitle}>Evoluci&oacute;n Mensual</h4><div className={s.chartInner}><Line data={evoData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true }, x: { grid: { display: false } } } }} /></div></div>}
          {tabla.length > 0 && (
            <div style={{ background: 'white', borderRadius: 14, padding: 16, marginBottom: 14, border: '1px solid var(--gray-light)' }}>
              <div className={s.rptSectionTitle}><span className={s.dot} />Mes a Mes</div>
              <div style={{ overflowX: 'auto' }}>
                <table className={s.rptTable}>
                  <thead><tr><th>Mes</th><th>Ventas</th><th>Monto $</th><th>Visitas</th><th>VRC</th><th>Cap.</th><th>Reun.</th><th>Score</th></tr></thead>
                  <tbody>{tabla.map((m, i) => <tr key={i}><td>{m.mes}</td><td>{m.ventas}</td><td>${(m.monto || 0).toLocaleString('es-AR')}</td><td>{m.visitas}</td><td>{m.vrc}</td><td>{m.captadas}</td><td>{m.reuniones}</td><td><strong>{(m.score || 0).toFixed(0)}</strong></td></tr>)}</tbody>
                </table>
              </div>
            </div>
          )}
        </>}
        <button className={s.btnSecondary} onClick={() => setView('resumen')} style={{ marginTop: 12 }}>← Volver a Resumen</button>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════
  //  PAGE: INFORME (PDF Overlay)
  // ═══════════════════════════════════════════════════
  const renderInforme = () => {
    const inf = informeRaw;
    const ag = inf.agente || {};
    const kpis = inf.kpis || {};
    const prod = inf.produccion || {};
    const comp = inf.compromisos || {};
    const evo = inf.evolucionMensual || [];
    const notas = inf.notasPorMes || [];
    const bloqs = inf.bloqueosPorReunion || [];
    const mejorM = inf.mejorMes;
    const peorM = inf.peorMes;
    const ins = inf.insignias || [];
    const rch = inf.racha || 0;
    const diasSin = inf.diasSinVenta;
    const frase = inf.fraseMotivacional;
    const agName = `${ag.name || ''} ${ag.last_name || ''}`.trim() || 'Agente';
    const initials = agName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    const totalScore = kpis.scoreTotal || kpis.score || 0;
    const conv = kpis.conversionVisitaVenta != null ? Number(kpis.conversionVisitaVenta).toFixed(1) : 0;
    const pctComp = comp.porcentaje || (comp.dados > 0 ? Math.round(comp.cumplidos / comp.dados * 100) : 0);
    const meses = inf.mesesActivos || 0;
    const totR = inf.totalReuniones || 0;

    const donutData = { datasets: [{ data: [comp.cumplidos || 0, Math.max(0, (comp.dados || 0) - (comp.cumplidos || 0))], backgroundColor: ['#27AE60', '#F5E6EC'], borderWidth: 0 }] };
    const evoData = evo.length > 0 ? {
      labels: evo.map(e => e.mes),
      datasets: [
        { label: 'Score', data: evo.map(e => e.score?.toFixed(1) || 0), borderColor: '#4B3A8C', backgroundColor: 'rgba(75,58,140,0.08)', borderWidth: 2, pointRadius: 4, tension: 0.3, fill: true },
        { label: 'Ventas', data: evo.map(e => e.ventasCerradas || 0), borderColor: '#8C3A5A', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 3, borderDash: [4, 3], tension: 0.3 }
      ]
    } : null;

    // Table rows
    const tableRows = evo.map((e, i) => (
      <tr key={i}><td>{e.mes}</td><td>{e.ventasCerradas ?? '—'}</td><td>${(e.montoVendido || 0).toLocaleString('es-AR')}</td><td>{e.visitasRealizadas ?? '—'}</td><td>{e.tasaciones ?? '—'}</td><td>{e.propiedadesCaptadas ?? '—'}</td><td>{e.posteosIG ?? '—'}</td><td><strong>{(e.score || 0).toFixed(0)}</strong></td></tr>
    ));

    return (
      <div className={s.page} style={{ maxWidth: 860 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <button className={s.btnSecondary} onClick={() => setView('resumen')}>← Volver</button>
          <button className={s.btnPdf} onClick={() => window.print()}>⬇ Descargar PDF</button>
        </div>

        {estadisticasLoading && !inf.agente ? <div className={s.loading}>Cargando informe...</div> : totR === 0 ? (
          <div className={s.emptyState}><div className={s.emptyEmoji}>📂</div><p className={s.emptyText}>No hay reuniones registradas para este agente.</p></div>
        ) : <>
          {/* Header */}
          <div className={s.rptHeader}>
            <div className={s.rptTop}>
              <div className={s.rptAvatar}>{initials}</div>
              <div>
                <div className={s.rptCompany}>by Ra&iacute;ces Soluciones Inmobiliarias</div>
                <div className={s.rptName}>{agName}</div>
                <div className={s.rptPeriod}>{meses} mes{meses !== 1 ? 'es' : ''} &middot; {totR} reuniones</div>
                <div className={s.rptBadges}>
                  <span className={`${s.rptBadge} ${s.rptBadgeBlue}`}>Score: {totalScore.toFixed(0)}</span>
                  <span className={`${s.rptBadge} ${s.rptBadgeRed}`}>Conv: {conv}%</span>
                  <span className={`${s.rptBadge} ${s.rptBadgeGold}`}>Comp: {pctComp}%</span>
                  {rch >= 2 && <span className={`${s.rptBadge} ${s.rptBadgeGold}`}>🔥 Racha: {rch}</span>}
                </div>
                {inf.director && <div className={s.rptDirector}>Director: <span className={s.rptDirectorSpan}>{inf.director}</span></div>}
              </div>
            </div>
          </div>

          {/* Insignias */}
          {ins.length > 0 && <div className={s.rptSection}><div className={s.rptSectionTitle}><span className={s.dot} />Insignias Ganadas</div><div>{ins.map((b, j) => <span key={j} className={s.insignia}>{b.icono} {b.label}</span>)}</div></div>}
          {diasSin > 30 && <div className={`${s.alert} ${s.alertWarn}`} style={{ marginBottom: 12 }}>⚠️ Llevas {diasSin} d&iacute;as sin cerrar una venta.</div>}
          {mejorM && <div className={`${s.alert} ${s.alertSuccess}`} style={{ marginBottom: 12 }}>🏆 Mejor mes hist&oacute;rico: <strong>{mejorM.mes}</strong> &mdash; Score {mejorM.score?.toFixed(0)}</div>}

          {/* KPI Grid */}
          <div className={s.rptKpiGrid}>
            <div className={s.rptKpi}><div className={s.rptKpiLabel}>Ventas Cerradas</div><div className={s.rptKpiValue}>{kpis.ventasCerradas ?? 0}</div><div className={s.rptKpiSub}>per&iacute;odo</div></div>
            <div className={s.rptKpi}><div className={s.rptKpiLabel}>Monto Total</div><div className={s.rptKpiValue}>{fmt$(kpis.montoVendido)}</div><div className={s.rptKpiSub}>sin redondeo</div></div>
            <div className={s.rptKpi}><div className={s.rptKpiLabel}>Visitas</div><div className={s.rptKpiValue}>{kpis.visitasRealizadas ?? 0}</div><div className={s.rptKpiSub}>conv. {conv}%</div></div>
            <div className={s.rptKpi}><div className={s.rptKpiLabel}>VRC</div><div className={s.rptKpiValue}>{kpis.tasaciones ?? 0}</div><div className={s.rptKpiSub}>{kpis.propiedadesCaptadas ?? 0} captadas</div></div>
          </div>

          {/* Producción */}
          {Object.keys(prod).length > 0 && (
            <div className={s.rptSection}><div className={s.rptSectionTitle}><span className={s.dot} />Producci&oacute;n</div>
              <div className={s.dSections}>
                <DRow label="Contactos" val={prod.contactos || 0} /><DRow label="Llamadas" val={prod.llamadas || 0} />
                <DRow label="Ofertas" val={prod.ofertas || 0} /><DRow label="Seguimientos" val={prod.seguimientos || 0} />
                <DRow label="Due&ntilde;os" val={prod.duenos || 0} /><DRow label="Compradores" val={prod.compradores || 0} />
              </div>
            </div>
          )}

          {/* Evolution Chart */}
          {evoData && <div className={s.rptSection}><div className={s.rptSectionTitle}><span className={s.dot} />Evoluci&oacute;n Mensual &mdash; Score</div><div className={s.rptChartWrap}><Line data={evoData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true, grid: { color: '#F0F0F8' } }, x: { grid: { display: false } } } }} /></div></div>}

          {/* Monthly Table */}
          {evo.length > 0 && (
            <div className={s.rptSection}><div className={s.rptSectionTitle}><span className={s.dot} />M&eacute;tricas Mes a Mes</div>
              <div style={{ overflowX: 'auto' }}>
                <table className={s.rptTable}>
                  <thead><tr><th>Mes</th><th>Ventas</th><th>Monto $</th><th>Visitas</th><th>VRC</th><th>Captadas</th><th>Posteos</th><th>Score</th></tr></thead>
                  <tbody>{tableRows}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* Best/Worst Month */}
          {(mejorM || peorM) && evo.length >= 2 && (
            <div className={s.rptBwGrid}>
              {mejorM && <div className={`${s.rptBwCard} ${s.rptBwBest}`}><div className={s.rptBwLabel} style={{ color: '#1E8449' }}>✦ Mejor mes</div><div className={s.rptBwMonth} style={{ color: '#1A6B38' }}>{mejorM.mes}</div><div className={s.rptBwDetail}>Score: {mejorM.score?.toFixed(0)} &middot; Ventas: {mejorM.ventas ?? '—'}</div></div>}
              {peorM && <div className={`${s.rptBwCard} ${s.rptBwWorst}`}><div className={s.rptBwLabel} style={{ color: '#C0392B' }}>▼ Mes con menor performance</div><div className={s.rptBwMonth} style={{ color: '#A93226' }}>{peorM.mes}</div><div className={s.rptBwDetail}>Score: {peorM.score?.toFixed(0)} &middot; Ventas: {peorM.ventas ?? '—'}</div></div>}
            </div>
          )}

          {/* Compromisos */}
          {(comp.dados || 0) > 0 && (
            <div className={s.rptSection}><div className={s.rptSectionTitle}><span className={s.dot} />Compromisos Cumplidos</div>
              <div className={s.rptCompGrid}>
                <div className={s.rptDonutWrap}>
                  <div className={s.rptDonutInner}>
                    <Doughnut data={donutData} options={{ responsive: true, maintainAspectRatio: false, cutout: '70%', plugins: { legend: { display: false } } }} />
                    <div className={s.rptDonutCenter}><div className={s.rptDonutPct}>{pctComp}%</div><div className={s.rptDonutLbl}>cumplidos</div></div>
                  </div>
                  <div className={s.rptDonutLegend}>{comp.cumplidos || 0} de {comp.dados || 0}</div>
                </div>
                <div className={s.rptCompRows}>
                  <div className={s.rptCompRow} style={{ background: '#EAF8F0', border: '1px solid #A8E6C0' }}><span className={s.rptCompRowLabel} style={{ color: '#1E8449' }}>Cumplidos</span><span className={s.rptCompRowVal} style={{ color: '#1A6B38' }}>{comp.cumplidos || 0}</span></div>
                  <div className={s.rptCompRow} style={{ background: '#FEF0F0', border: '1px solid #F5BBBB' }}><span className={s.rptCompRowLabel} style={{ color: '#C0392B' }}>Pendientes</span><span className={s.rptCompRowVal} style={{ color: '#A93226' }}>{comp.pendientes || 0}</span></div>
                  <div className={s.rptCompRow} style={{ background: 'var(--cream)', border: '1px solid var(--gray-light)' }}><span className={s.rptCompRowLabel} style={{ color: 'var(--gray)' }}>Total dados</span><span className={s.rptCompRowVal} style={{ color: 'var(--dark)' }}>{comp.dados || 0}</span></div>
                </div>
              </div>
            </div>
          )}

          {/* Bloqueos */}
          {bloqs.length > 0 && (
            <div className={s.rptSection}><div className={s.rptSectionTitle}><span className={s.dot} />Bloqueos Reportados</div>
              {bloqs.map((b, i) => <div key={i} className={s.rptNotaItem} style={{ borderLeftColor: '#C0392B' }}><div className={s.rptNotaMeta}>⚠ {formatDate(b.fecha)}</div><div className={s.rptNotaText}>{b.bloqueos}</div></div>)}
            </div>
          )}

          {/* Director Observations */}
          <div className={s.rptSection}><div className={s.rptSectionTitle}><span className={s.dot} />Observaciones del Director</div>
            <div className={s.rptDirectorObs}><label className={s.rptDirectorObsLabel}>Notas del Director</label><textarea className={s.rptDirectorObsTextarea} placeholder={`Escribí observaciones personalizadas para ${agName}...`} /></div>
          </div>

          {/* Notas */}
          <div className={s.rptSection}><div className={s.rptSectionTitle}><span className={s.dot} />Notas de Reuniones</div>
            {notas.length > 0 ? notas.map((nm, i) => (nm.notas || []).map((n, j) => (
              <div key={`${i}-${j}`} className={s.rptNotaItem}><div className={s.rptNotaMeta}>{nm.mes} &middot; Reuni&oacute;n {n.reunion || (j + 1)}</div><div className={s.rptNotaText}>{n.texto || n}</div></div>
            ))) : <p style={{ color: 'var(--gray)', fontSize: 12 }}>Sin notas.</p>}
          </div>

          {/* Frase */}
          {frase && <div className={s.rptFooter}><div><div className={s.rptFooterBrand}>by Ra&iacute;ces Soluciones Inmobiliarias</div><div className={s.rptFooterText}>&ldquo;{frase}&rdquo;</div></div></div>}
        </>}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════
  //  EDIT MODAL
  // ═══════════════════════════════════════════════════
  const renderEditModal = () => {
    if (!editOpen) return null;
    const ef = editForm;
    const hE = (k, v) => setEditForm(prev => ({ ...prev, [k]: v }));
    return (
      <div className={s.editModal} onClick={closeEdit}>
        <div className={s.editPanel} onClick={e => e.stopPropagation()}>
          <h3 className={s.editPanelTitle}>✏️ Editar Reuni&oacute;n</h3>
          <div className={s.metaGrid}>
            <div className={s.field}><label className={s.fieldLabel}>Ventas</label><input type="number" className={s.fieldInput} min="0" value={ef.ventas} onChange={e => hE('ventas', e.target.value)} /></div>
            <div className={s.field}><label className={s.fieldLabel}>Monto $</label><input type="number" className={s.fieldInput} min="0" value={ef.monto} onChange={e => hE('monto', e.target.value)} /></div>
            <div className={s.field}><label className={s.fieldLabel}>Visitas</label><input type="number" className={s.fieldInput} min="0" value={ef.visitas} onChange={e => hE('visitas', e.target.value)} /></div>
            <div className={s.field}><label className={s.fieldLabel}>VRC</label><input type="number" className={s.fieldInput} min="0" value={ef.vrc} onChange={e => hE('vrc', e.target.value)} /></div>
            <div className={s.field}><label className={s.fieldLabel}>Captadas</label><input type="number" className={s.fieldInput} min="0" value={ef.captadas} onChange={e => hE('captadas', e.target.value)} /></div>
            <div className={s.field}><label className={s.fieldLabel}>Posteos IG</label><input type="number" className={s.fieldInput} min="0" value={ef.posteos} onChange={e => hE('posteos', e.target.value)} /></div>
            <div className={s.field}><label className={s.fieldLabel}>Comp. Dados</label><input type="number" className={s.fieldInput} min="0" value={ef.compDados} onChange={e => hE('compDados', e.target.value)} /></div>
            <div className={s.field}><label className={s.fieldLabel}>Comp. Cumplidos</label><input type="number" className={s.fieldInput} min="0" value={ef.compCumplidos} onChange={e => hE('compCumplidos', e.target.value)} /></div>
            <div className={`${s.field} ${s.span2}`}><label className={s.fieldLabel}>Objetivo</label><input type="text" className={s.fieldInput} value={ef.objetivo} onChange={e => hE('objetivo', e.target.value)} /></div>
            <div className={`${s.field} ${s.span2}`}><label className={s.fieldLabel}>Notas libres</label><textarea className={`${s.fieldInput} ${s.fieldTextarea}`} style={{ minHeight: 60 }} value={ef.notas} onChange={e => hE('notas', e.target.value)} /></div>
          </div>
          <div className={s.editActions}>
            <button className={s.btnSecondary} onClick={closeEdit}>Cancelar</button>
            <button className={s.btnPrimary} onClick={saveEdit}>💾 Guardar cambios</button>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════
  //  MAIN RENDER
  // ═══════════════════════════════════════════════════
  return (
    <div className={s.container}>
      <Nav />
      {view === 'inicio' && renderInicio()}
      {view === 'nueva' && renderNueva()}
      {view === 'historial' && renderHistorial()}
      {view === 'resumen' && renderResumen()}
      {view === 'comparativa' && renderComparativa()}
      {view === 'anual' && renderAnual()}
      {view === 'informe' && renderInforme()}
      {renderEditModal()}
      {confirmDel && (
        <div className={s.confirmOverlay} onClick={() => setConfirmDel(null)}>
          <div className={s.confirmBox} onClick={e => e.stopPropagation()}>
            <p>¿Eliminar {confirmDel.label}?</p>
            <div className={s.confirmActions}>
              <button className={s.btnSecondary} onClick={() => setConfirmDel(null)}>Cancelar</button>
              <button className={s.btnPrimary} style={{ background: '#E74C3C' }} onClick={() => handleDelete(confirmDel.id)}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
      {toast && <div className={`${s.toast} ${toast.ok ? s.toastOk : s.toastError}`}>{toast.msg}</div>}
    </div>
  );
};

export default ReunionesStats;
